import DestroyableTokenListWithCallbacks from './DestroyableTokenListWithCallbacks';
import { DestroyableTokenListWithCallbacksFactory } from '../types';

/**
 * Manages classes on a DOM element which may have multiple instances of any given class
 *
 * This is for use in cases where multiple agents may want to add the same class
 * to a single element, but the element should not have that class removed until
 * all agents have requested it to be removed.
 *
 * When the count for a class rises above 0, the class is added to the element.
 * If it drops to 0 or fewer, the class is removed. Classes which were not
 * previously being counted are assumed to be 0 following the first addition or
 * removal.
 *
 * Class counts are incremented using connected class lists, which are
 * an implementation of the `DOMTokenList` interface. The maximum possible count
 * for any given class in the manager is equal to the number of connected
 * class lists.
 *
 * A class list is created using the `.createClassList()` method. This list
 * will initially be empty. Its contents can be modified in any of the same
 * ways a normal `Element` instance's class list can be modified.
 *
 */
export default class DOMElementMultiSetClassManager {

    /**
     * Collection of all known class lists
     */
    protected classLists = new Set<DOMTokenList>();

    constructor(
        /**
         * Query selector to target the element (only supports one result)
         */
        public selector: string,
        /**
         * The document to which the query selector will be applied
         * @private
         */
        private document = window.document
    ) { }

    /**
     * Create a class list that can be used to track use of classes by one particular consumer
     *
     * @param classListFactory If provided, this factory will be used to create the class list instance
     */
    public createClassList(classListFactory?: DestroyableTokenListWithCallbacksFactory) {
        const factory = (
            classListFactory ||
            ((a, r, d) => this.defaultClassListFactory(a, r, d))
        );
        const classList = factory(
            (className: string) => this.onAdd(classList, className),
            (className: string) => this.onRemove(classList, className),
            () => this.onDestroy(classList)
        );
        this.classLists.add(classList);
        return classList;
    }

    /**
     * Count the number of times a given class appears
     */
    public getClassCount(className: string) {
        return Array.from(this.classLists).reduce((n, cl) => n + +cl.contains(className), 0);
    }

    /**
     * Factory that generates new destroyable class lists if none is specified when createClassList() is called
     *
     * @param onAdd
     * @param onRemove
     * @param onDestroy
     */
    public defaultClassListFactory(
        onAdd: (token: string) => void,
        onRemove: (token: string) => void,
        onDestroy: () => void
    ) {
        return new DestroyableTokenListWithCallbacks(onAdd, onRemove, onDestroy);
    }

    protected get element() {
        return this.document.querySelector(this.selector);
    }

    /**
     * Increment the number of times a class appears on the element by 1
     *
     * Updates the class list automatically.
     *
     * @param className The class to increment
     */
    protected onAdd(
        classList: DOMTokenList,
        className: string
    ) {
        this.element.classList.add(className);
    }

    /**
     * Decrement the number of times a class appears on an element by 1
     *
     * Updates the class list automatically.
     *
     * @param className The class to decrement
     */
    protected onRemove = (
        classList: DOMTokenList,
        className: string
    ) => {
        if (this.getClassCount(className) <= 0) {
            this.element.classList.remove(className);
        }
    }

    /**
     * Called when a class list is about to be destroyed.
     *
     * This removes the internal reference to the class list and removes all
     * classes from the targeted element that have no additional occurrences
     * outside of the class list that is being currently being destroyed.
     *
     * @param classList
     * @param classNames
     */
    protected onDestroy(classList: DOMTokenList) {
        this.classLists.delete(classList);
        const {element} = this;
        classList.forEach((className) => {
            if (this.getClassCount(className) <= 0) {
                element.classList.remove(className);
            }
        });
    }
}
